﻿<template>
	<div ref="el" class="h-100 d-flex flex-column">
		<GridButtons :tableButtons="tableButtons" />
		<AgGridVue v-if="gridIsVisible" :columnDefs="colDefs" :tabIndex="tabIndex" @gridReady="onGridReady" :gridOptions="gridOptions" :columnTypes="tableDefaultDataTypes" v-model="tableLocalData" class="ag-theme-quartz flex-grow-1" :enableRtl="true" :pinnedBottomRowData="pinnedBottomRow" :getContextMenuItems="getContextMenuItems" :getMainMenuItems="getMainMenuItems"></AgGridVue>
	</div>
</template>
<script lang="ts" setup generic="T extends TDefaultFields">
	import { AgGridVue } from "ag-grid-vue3"
	import { GridReadyEvent, IRowNode, CellKeyDownEvent, MenuItemDef } from 'ag-grid-enterprise'
	import "ag-grid-community/styles/ag-grid.css"
	import "ag-grid-community/styles/ag-theme-quartz.css"
	import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
	import { LicenseManager } from 'ag-grid-enterprise'
	import tableDefaultDataTypes from './data-types'
	import { generateUUID, idGenerator } from '@/plugins/helper'
	import $sc from '~/plugins/shortcut.collection.js'
	import Merge from 'lodash/merge'
	import { i18nInstance } from '~/plugins/localization'
	import { avg, count, getEmptyStructure, max, min, removeTableFieldsFromData, shortcutDisplay, sum } from './plugins/utils'
	import { RowStates } from '@/enums/system'
	import { IActionDefinition, IAggregateFunc, IExtendedColDef, IExtendedGridApi, IExtendedGridOptions, IValidator, tableFields, TDefaultFields, TObjectWithFieldOf, TTargetRowIndex } from './interfaces'
	import sortBy from 'lodash/sortBy'
	import { toastInstance } from '@/plugins/toast'
	import { POSITION, TYPE } from 'vue-toastification'
	import CloneDeep from 'lodash/cloneDeep'
	import isEqual from 'lodash/isEqual'
	import fsJson from '~/assets/resources/fa.json'
	import GridButtons from './custom-components/grid-buttons.vue'
	import { useStore } from 'vuex'
	import emptyId from '~/statics/empty-id.js'

	LicenseManager.setLicenseKey("DownloadDevTools_COM_NDEwMjM0NTgwMDAwMA==59158b5225400879a12a96634544f5b6")

	const props = defineProps<{
		data: T[],
		columns: IExtendedColDef[]
		options: IExtendedGridOptions<T>
		buttons?: any
		tabIndex: number
		actions?: IActionDefinition[]
		shortcuts?: IActionDefinition[]
	}>()

	const emit = defineEmits(['update:modelValue', 'data-changed', 'row-data-updated', 'cell-focused'])

	const t = i18nInstance.global.t

	const store = useStore()

	const gridIsVisible = ref(false)
	let gridSaveStateTimeout: number

	const el = ref(null)
	const tableButtons = ref<any[]>([])

	const gridApi = ref<IExtendedGridApi<T>>()
	const colDefs = ref<IExtendedColDef<T>[]>([])
	const tableLocalData = ref<T[]>([])

	const pinnedBottomRow = ref<any[]>([])
	const aggregateColumns = ref<TObjectWithFieldOf<T, IAggregateFunc<T>>>({})
	const isAggregationEnabled = ref<boolean>(true)

	let tableValidatorsMap = new Map<string, IValidator[]>()

	const addRowOnLoadTimeout = ref<number | null>(null)

	const activeElementForPaste = ref<HTMLInputElement | null>(null)

	const SUPPRESSED_KEYS = ['Enter', 'Escape']

	const defaultKeyHandlers: IActionDefinition[] = [
		{
			name: 'insertRow',
			shortcut: $sc.find('insert-new-row', 'table'),
			handler: () => {
				if (tableButtons.value?.length && tableButtons.value?.findIndex((btn: any) => btn.name === 'addRow') !== -1)
					addNewRow('active')
			}
		}
	]

	const gridOptions = ref<IExtendedGridOptions<T>>({
		gridId: '',
		headerHeight: 30,
		animateRows: false,
		defaultColDef: {
			suppressKeyboardEvent: (params) => {
				if (params.event.key === 'Enter' && !params.editing && !params.event.shiftKey && !params.event.ctrlKey && !params.event.altKey)
					return false

				if (SUPPRESSED_KEYS.includes(params.event.key)) {
					params.event.preventDefault()
					return true
				}
				return false
			},
			cellStyle: {
				alignItems: 'center',
				display: 'flex'
			},
			editable: (params) => {
				if (!params.data) return false

				if (gridOptions.value.isEditable === false)
					return false

				if (params.data[tableFields.id].indexOf('aggregate') > -1) return false

				if (params.data?.rowState === 'deleted') return false

				if (params.data?.[tableFields.isLocked]) return false

				let canEdit = false
				const colDef: IExtendedColDef<T> = params.colDef as IExtendedColDef

				if (typeof colDef.isEditable === 'function')
					canEdit = colDef.isEditable(params)
				else canEdit = !!colDef.isEditable

				if (!canEdit) return false

				if (typeof colDef.entering === 'function')
					canEdit = colDef.entering(params.data as T)
				else if (!colDef.entering && colDef.entering !== false)
					canEdit = true
				else canEdit = !!colDef.entering

				return canEdit

			},
			cellClassRules: {
				'cell-invalid': (params) => params.data ? params.data[tableFields.invalidCells]?.includes(params.colDef.field || '') : false
			}
		},
		getRowId: (params) => params.data[tableFields.id],
		rowSelection: {
			mode: 'multiRow',
			enableSelectionWithoutKeys: false,
			checkboxes: false,
			enableClickSelection: true,
			headerCheckbox: false,
		},
		rowHeight: 30,
		stopEditingWhenCellsLoseFocus: false,
		localeText: fsJson['ag-grid-local'] as any,
		rowClassRules: {
			'row-deleted': (params) => !!(params.data && params.data.rowState === RowStates.Deleted),
			'row-new': (params) => !!(params.data && params.data.rowState === RowStates.New),
			'row-changed': (params) => !!(params.data && params.data.rowState === RowStates.Changed),
			'row-invalid': (params) => !!(params.data && !params.data[tableFields.isValid]),
			'row-locked': (params) => {
				if (!params.data) return false

				if (gridOptions.value.rowIsLocked && gridOptions.value.rowIsLocked(params.data)) {
					setTimeout(() => {
						params.data![tableFields.isLocked] = true
					}, 100)
					return true
				}

				setTimeout(() => {
					params.data![tableFields.isLocked] = false
				}, 100)
				return false
			},
		},
		suppressPropertyNamesCheck: true,
		canAddRow: true,
		addRowOnMount: true,
		defaultExcelExportParams: {
			processCellCallback: (params) => {
				const colDef = params.column?.getColDef()


				if (!colDef || !params.node) return params.value

				if (params.node?.data[tableFields.rowType] === 'aggregate')
					return ''

				try {

					if (colDef.valueGetter && typeof colDef.valueGetter === 'function') {
						return colDef.valueGetter({ ...params, data: params.node!.data, colDef, node: params.node!, getValue: () => params.value })
					}

					// if (colDef.cellRenderer && typeof colDef.cellRenderer === 'function') {
					// 	return colDef.cellRenderer({ ...params, data: params.node!.data, colDef, node: params.node! })
					// }

					if (colDef.valueFormatter && typeof colDef.valueFormatter === 'function') {
						return params.formatValue(params.value)
					}

					return params.value
				}
				catch {
					return params.value
				}
			}
		},
		defaultCsvExportParams: {
			processCellCallback: (params) => {
				const colDef = params.column?.getColDef()


				if (!colDef || !params.node) return params.value

				if (params.node?.data[tableFields.rowType] === 'aggregate')
					return ''

				try {

					if (colDef.valueGetter && typeof colDef.valueGetter === 'function') {
						return colDef.valueGetter({ ...params, data: params.node!.data, colDef, node: params.node!, getValue: () => params.value })
					}

					if (colDef.cellRenderer && typeof colDef.cellRenderer === 'function') {
						return colDef.cellRenderer(params)
					}

					if (colDef.valueFormatter && typeof colDef.valueFormatter === 'function') {
						return params.formatValue(params.value)
					}

					return params.value
				}
				catch {
					return params.value
				}
			}
		},
		onRowSelected(event) {
			if (event.node?.isSelected())
				emit('update:modelValue', [event.data])
		},
		onCellValueChanged(params) {
			if (!isEqual(params.newValue, params.oldValue)) {
				calculateAllAggregations()
				emit('data-changed', params)
			}
		},
		onRowDataUpdated(params) {
			calculateAllAggregations()
			emit('row-data-updated', params)
		},
		onCellMouseDown(event) {
			const mouseEvent = event.event as MouseEvent

			if (mouseEvent?.which === 3) {
				if ((mouseEvent.target as HTMLElement).tagName === 'INPUT') {
					activeElementForPaste.value = mouseEvent.target as HTMLInputElement
				}
				else {
					activeElementForPaste.value = null
				}
			}
			else {
				activeElementForPaste.value = null
			}
		},
		onCellKeyDown(params: CellKeyDownEvent) {
			const event = params.event as KeyboardEvent

			if (props.shortcuts?.length) {
				for (let i = 0; i < props.shortcuts?.length; i++) {
					const shortcutAction = props.shortcuts[i]
					if (event && event.keyCode === shortcutAction.shortcut.keyCode && event.shiftKey === shortcutAction.shortcut.shiftKey && event.altKey === shortcutAction.shortcut.altKey && event.ctrlKey === shortcutAction.shortcut.ctrlKey) {
						shortcutAction.handler(params.data, params.rowIndex, tableLocalData.value, params.column, params.api)
					}

				}
			}


			if (props.actions?.length) {
				for (let i = 0; i < props.actions?.length; i++) {
					const action = props.actions[i]
					if (event && event.keyCode === action.shortcut.keyCode && event.shiftKey === action.shortcut.shiftKey && event.altKey === action.shortcut.altKey && event.ctrlKey === action.shortcut.ctrlKey) {
						action.handler(params.data, params.rowIndex, tableLocalData.value, params.column, params.api)
					}

				}
			}

			for (let i = 0; i < defaultKeyHandlers.length; i++) {
				const keyHandler = defaultKeyHandlers[i]
				if (event && event.keyCode === keyHandler.shortcut.keyCode && event.shiftKey === keyHandler.shortcut.shiftKey && event.altKey === keyHandler.shortcut.altKey && event.ctrlKey === keyHandler.shortcut.ctrlKey) {
					keyHandler.handler(params.data, params.rowIndex, tableLocalData.value, params.column, params.api)
				}
			}
		},
		focusGridInnerElement(params) {
			if (gridOptions.value.isEditable) {
				focusOnFirstEditableCellOfRow(0)
				return true
			}
			return false
		},
		onColumnVisible() {
			saveGridState()
		},
		onColumnResized() {
			saveGridState()
		},
		onColumnMoved() {
			saveGridState()
		},
		onCellFocused(event) {
			emit('cell-focused', event)
		},
		maintainColumnOrder: true,
		singleClickEdit: true,
		suppressDragLeaveHidesColumns: true,
		prepareDataOnMount: true
	})

	const getActiveState = computed(() => store.getters['appStates/getActiveByKey'](`grid__${props.options.gridId}`))

	onMounted(() => {
		prepareOptions(props.options)
		computeButtons(props.buttons)
		prepareColumns(props.columns)

		if (gridOptions.value.prepareDataOnMount)
			prepareData(props.data)

		tryGetGridState()

		if (isAggregationEnabled.value) {
			prepareAggregationColumns()
		}
	})

	onBeforeUnmount(() => {
		if (gridSaveStateTimeout)
			clearTimeout(gridSaveStateTimeout)
	})

	function onGridReady(params: GridReadyEvent) {
		gridApi.value = params.api

		gridApi.value!.validateRowData = validateRowData
		gridApi.value!.addNewRow = addNewRow
		gridApi.value!.tryDeleteLastEmptyRow = tryDeleteLastEmptyRow
	}


	function computeButtons(buttons = {}) {
		let buttonsObj = Merge(getDefaultButtons(), buttons)
		let buttonsArray: any[] = Object.values(buttonsObj) || []

		buttonsArray.map((x => {
			x.id ? x.id : x.id = idGenerator(5)
			x.options.visible ? x.options.visible : true
			x.options.disabled ? x.options.disabled : false
			return x
		}))

		buttonsArray = buttonsArray.filter(x => x.options.visible !== false)
		tableButtons.value = buttonsArray.sort((a, b) => (a.displayOrder > b.displayOrder) ? 1 : -1)
	}

	function getDefaultButtons() {
		return {
			addBtn: {
				id: idGenerator(5),
				displayOrder: 1,
				class: "mr-2 no-outline",
				type: 'nk-button',
				name: 'addRow',
				options: {
					disabled: gridOptions.value.isEditable === false,
					visible: false,
					isDanger: false,
					tooltipEnabled: true,
					hideTitle: true,
					icon: 'plus',
					title: t('table.buttons.add'),
					element: () => el.value,
					hideShortcut: true,
					handler: () => {
						addNewRow('end')
					},
				}
			},
			duplicateBtn: {
				id: idGenerator(5),
				displayOrder: 2,
				class: "mr-2 no-outline",
				type: 'nk-button',
				options: {
					disabled: gridOptions.value.isEditable === false,
					visible: false,
					icon: 'copy',
					isDanger: false,
					tooltipEnabled: true,
					hideTitle: true,
					title: i18nInstance.global.t('table.buttons.copy'),
					shortcut: $sc.find('copy', 'table'),
					element: () => el.value,
					hideShortcut: true,
					handler: () => copyRow(),
				}
			},
			upBtn: {
				id: idGenerator(5),
				displayOrder: 3,
				class: "mr-2 no-outline",
				type: 'nk-button',
				options: {
					disabled: gridOptions.value.isEditable === false,
					visible: false,
					icon: 'arrow-up',
					isDanger: false,
					tooltipEnabled: true,
					hideTitle: true,
					title: i18nInstance.global.t('table.buttons.up'),
					shortcut: $sc.find('move-up', 'table'),
					element: () => el.value,
					hideShortcut: true,
					handler: () => moveRow('up'),
				}
			},
			downBtn: {
				id: idGenerator(5),
				displayOrder: 4,
				class: "mr-2 no-outline",
				type: 'nk-button',
				options: {
					disabled: gridOptions.value.isEditable === false,
					visible: false,
					icon: 'arrow-down',
					isDanger: false,
					tooltipEnabled: true,
					hideTitle: true,
					title: i18nInstance.global.t('table.buttons.down'),
					shortcut: $sc.find('move-down', 'table'),
					element: () => el.value,
					hideShortcut: true,
					handler: () => moveRow('down'),
				}
			},
			deleteBtn: {
				id: idGenerator(5),
				displayOrder: 6,
				class: "mr-2 no-outline",
				element: () => el.value,
				type: 'nk-button',
				options: {
					disabled: gridOptions.value.isEditable === false,
					visible: false,
					icon: 'trash',
					isOutlineDanger: true,
					tooltipEnabled: true,
					hideTitle: true,
					title: i18nInstance.global.t('table.buttons.delete'),
					shortcut: $sc.find('delete-row', 'table'),
					element: () => el.value,
					hideShortcut: true,
					handler: () => deleteRow(),
				}
			},
		}
	}


	//#region Row Methods
	function updateRowData(newData: T[]) {
		gridApi.value?.applyTransaction({ update: newData })

		calculateAllAggregations()
	}

	function getNewRowData(prevRow?: IRowNode<T>) {
		const emptyObject = gridOptions.value.prepareNewRow ? gridOptions.value.prepareNewRow(prevRow?.data) : getEmptyStructure(gridApi.value?.getDisplayedRowAtIndex(0)?.data as any) as T

		return emptyObject
	}

	function addNewRow(targetIndex: TTargetRowIndex = 'end', focus: boolean = true, data?: T) {
		if (!gridApi.value) return

		const index = getTargetIndex(targetIndex)
		if (!index && index !== 0) return

		const prevRow = gridApi.value?.getDisplayedRowAtIndex(index - 1)

		const row = data ? data : getNewRowData(prevRow)

		addCustomFieldsToObject(row)

		row.displayOrder = index + 1

		const updatedRows = getReorderedRows(index + 1)

		gridApi.value.applyTransaction({ add: [row], addIndex: index, update: updatedRows })

		if (focus)
			focusOnFirstEditableCellOfRow(index)
	}

	function addMultipleNewRows(rowsData: T[], targetIndex: TTargetRowIndex = 'end', focus: boolean = true) {
		if (!gridApi.value) return

		const index = getTargetIndex(targetIndex)
		if (!index && index !== 0) return

		// Prepare new rows with updated displayOrder
		const newRows = rowsData.map((row, i) => {
			const newRow = { ...row }
			addCustomFieldsToObject(newRow)
			newRow.displayOrder = index + i + 1
			return newRow
		})

		// Reorder existing rows
		const updatedRows = getReorderedRows(index + 1, 'add', rowsData.length)

		// Apply transaction
		gridApi.value.applyTransaction({
			add: newRows,
			addIndex: index,
			update: updatedRows
		})

		// Focus on the first editable cell of the first new row
		if (focus && rowsData.length > 0) {
			focusOnFirstEditableCellOfRow(index)
		}
	}

	function getReorderedRows(startIndex: number, type: 'add' | 'delete' = 'add', count: number = 1) {
		const rowsData: T[] = []
		let lastDisplayOrder = 0

		gridApi.value!.forEachNode((node) => {
			if (!node.data) return

			if (node.data.displayOrder >= startIndex) {
				node.data.displayOrder += type === 'add' ? count : -Math.abs(count)

				if (node.data.rowState === RowStates.Unchanged)
					node.data.rowState = RowStates.Changed

				rowsData.push(node.data)
			}

			if (node.data.displayOrder > lastDisplayOrder)
				lastDisplayOrder = node.data.displayOrder
		})

		return rowsData
	}

	function getTargetIndex(targetIndex: TTargetRowIndex = 'end'): number | null {
		if (targetIndex === 'end')
			return tableLocalData.value.length
		else if (targetIndex === 'start')
			return 0
		else if (targetIndex === 'active') {
			const activeRow = gridApi.value?.getSelectedRows().at(-1)

			if (activeRow)
				return activeRow.displayOrder
			else
				return tableLocalData.value.length
		}
		else
			return targetIndex
	}

	function getActiveRowIndex() {
		if (!gridApi.value) return -1

		const activeNode = gridApi.value?.getSelectedNodes().at(-1)

		return activeNode ? activeNode.rowIndex : -1
	}

	function deleteRow() {
		const nodes = gridApi.value?.getSelectedNodes() || []

		for (const node of nodes) {
			deleteTargetRow(node)
		}

	}

	function deleteTargetRow(node: IRowNode<T>) {
		const data = node.data
		if (!data || !gridApi.value) return

		if (gridOptions.value.prepareDeleteRow && !gridOptions.value.prepareDeleteRow(data)) return

		const currentIndex = node.rowIndex
		const isLastRow = currentIndex === gridApi.value.getDisplayedRowCount() - 1

		// UnDelete
		if (data.rowState === RowStates.Deleted) {
			data.rowState = RowStates.Changed
			updateRowData([data])
		}

		// Delete for Server
		else if ([RowStates.Changed, RowStates.Unchanged].includes(data.rowState) || !data.rowState) {
			data.rowState = RowStates.Deleted
			updateRowData([data])
		}

		// Delete For Client
		else {
			gridApi.value?.applyTransaction({
				remove: [data],
				update: getReorderedRows(data.displayOrder, 'delete')
			})

			if (gridApi.value.getDisplayedRowCount() > 0) {
				if (isLastRow) {
					const newIndex = Math.max(0, currentIndex - 1)
					// After deleting the last row, focus the first editable cell of the new last row.
					const targetNode = gridApi.value?.getDisplayedRowAtIndex(newIndex)
					targetNode?.setSelected(true, true)
					focusOnFirstEditableCellOfRow(newIndex, false)
				} else if (currentIndex !== null) {
					// Otherwise, focus the first editable cell of the row that shifted into this index.
					const targetNode = gridApi.value?.getDisplayedRowAtIndex(currentIndex)
					targetNode?.setSelected(true, true)
					focusOnFirstEditableCellOfRow(currentIndex, false)
				}
			}
		}

		emit('data-changed', { api: gridApi.value })
	}

	function moveRow(dir: 'up' | 'down') {
		if (!gridApi.value) return

		const selectedNodes = gridApi.value.getSelectedNodes()
		if (selectedNodes.length !== 1) return

		const currentNode = selectedNodes[0]
		const currentRowIndex = currentNode.data?.displayOrder
		const allRows: IRowNode[] = []

		if (!currentRowIndex) return

		gridApi.value.forEachNode((node) => allRows.push(node))
		const targetRowIndex = dir === 'up' ? currentRowIndex - 1 : currentRowIndex + 1

		if (targetRowIndex < 1 || targetRowIndex > allRows.length) return

		const targetNode = allRows.find(x => x.data.displayOrder === targetRowIndex)

		if (!targetNode) return

		const tempOrder = currentNode.data!.displayOrder
		currentNode.data!.displayOrder = targetNode.data.displayOrder
		currentNode.data!.rowState = currentNode.data!.rowState === RowStates.Unchanged ? RowStates.Changed : currentNode.data!.rowState
		targetNode.data.displayOrder = tempOrder
		targetNode.data.rowState = targetNode.data!.rowState === RowStates.Unchanged ? RowStates.Changed : targetNode.data!.rowState

		gridApi.value.applyTransaction({ update: [currentNode.data, targetNode.data] })

		gridApi.value.deselectAll()
		gridApi.value.getRowNode(currentNode.id as string)!.setSelected(true)
	}

	function copyRow() {
		if (!gridApi.value) return

		const selectedNodes = gridApi.value.getSelectedNodes()
		if (selectedNodes.length === 0) return

		for (const node of selectedNodes) {
			let rowData = CloneDeep(node.data) as T

			if (gridOptions.value.onCopyRow)
				rowData = gridOptions.value.onCopyRow(rowData)

			addNewRow('end', true, rowData)

		}
	}

	function addCustomFieldsToObject(rowData: T) {
		rowData.rowState = RowStates.New
		rowData[tableFields.id] = `grid__${generateUUID()}`
		rowData[tableFields.isValid] = true
		rowData[tableFields.invalidCells] = []
		rowData[tableFields.rowType] = 'row'

		return rowData
	}

	function focusOnFirstEditableCellOfRow(rowIndex: number, selectRowNode: boolean = true) {
		if (!gridApi.value) return

		const allColumns = gridApi.value.getAllGridColumns()

		let newColumnIndex = 0
		let foundEditableCell = false
		let node: IRowNode | null = null

		// Search for an editable cell in the specified row
		while (!foundEditableCell) {
			const rowNode = gridApi.value.getDisplayedRowAtIndex(rowIndex)
			const targetColumn = allColumns[newColumnIndex]

			if (rowNode && targetColumn.isCellEditable(rowNode)) {
				foundEditableCell = true
				node = rowNode
			} else {
				newColumnIndex += 1

				// Wrap within the row's columns if no editable cell is found
				if (newColumnIndex >= allColumns.length) return
			}
		}
		if (foundEditableCell) {
			gridApi.value?.stopEditing()
			gridApi.value?.startEditingCell({
				rowIndex: rowIndex,
				colKey: allColumns[newColumnIndex].getId(),
			})
			if (selectRowNode)
				node?.setSelected(true, true)
		}
	}

	function tryDeleteLastEmptyRow() {
		if (!gridApi.value) return false

		const editingCell = gridApi.value.getEditingCells()?.[0]
		gridApi.value.stopEditing()

		const rowCount = gridApi.value.getDisplayedRowCount()
		if (rowCount === 0) return false

		const lastRowNode = gridApi.value.getDisplayedRowAtIndex(rowCount - 1)

		if (!lastRowNode || !lastRowNode.data) return false

		const emptyRow = getNewRowData()

		const isEmpty = isEqual(removeTableFieldsFromData(lastRowNode.data), removeTableFieldsFromData(emptyRow))

		if (isEmpty)
			deleteTargetRow(lastRowNode)
		else if (editingCell)
			gridApi.value.startEditingCell({
				rowIndex: editingCell.rowIndex,
				colKey: editingCell.column.getId()
			})


		return isEmpty
	}
	//#endregion Row Methods

	//#region Validators

	function validateSelectedRow() {
		const selectedNodes = gridApi.value!.getSelectedNodes()
		if (selectedNodes.length !== 1) return

		validateRowData(selectedNodes[0].data as T)
	}

	function validateAllRows() {
		if (!gridApi.value) return

		gridApi.value.stopEditing()

		const rows: T[] = []
		const errors: string[] = []

		gridApi.value.forEachNode((node) => {
			if (!node.data) return

			if (node.data?.rowState === RowStates.Deleted)
				rows.push(node.data)

			else {
				const validateObj = validateRowData(node.data, false)
				rows.push(validateObj.row)

				if (validateObj.message)
					errors.push(validateObj.message)
			}
		})

		if (errors.length)
			toastInstance(errors.join('\n'), { type: TYPE.ERROR, position: POSITION.TOP_CENTER })


		gridApi.value.applyTransaction({ update: rows })

		return !errors.length
	}

	function validateRowData(row: T, apply: boolean = true, oldRow?: T) {
		let isValid = true
		const invalidCells: string[] = []
		let errorsMessage: string[] = []

		tableValidatorsMap.forEach((validators, key) => {
			const field = key.split('$')[0]
			const headerName = key.split('$')[1]

			const cellValue = row[field as keyof T]

			let isCellValid = !validators.map(x => {
				const testPassed = x.test(cellValue, row)

				if (!testPassed)
					errorsMessage.push(x.error(headerName ? headerName : field, row))

				return testPassed

			}).some(x => x === false)

			if (!isCellValid) {
				invalidCells.push(field)
				isValid = false
			}
		})

		if (oldRow) {
			oldRow[tableFields.invalidCells] = invalidCells
			oldRow[tableFields.isValid] = isValid
		} else {
			row[tableFields.invalidCells] = invalidCells
			row[tableFields.isValid] = isValid
		}

		const errorMessage = `در ردیف ${row.displayOrder}: \n \t${errorsMessage.join('\n \t')}`

		if (apply) {
			gridApi.value?.applyTransaction({ update: [oldRow ? oldRow : row] })

			if (errorsMessage.length)
				toastInstance(errorMessage, { type: TYPE.ERROR, position: POSITION.TOP_CENTER })
		}

		return {
			row: row,
			isValid: isValid,
			message: errorsMessage.length ? errorMessage : null
		}
	}
	//#endregion Validators

	//#region Table Methods
	function prepareData(data: T[]) {
		let lastRowIndex = 0

		const aggColNames = Object.keys(aggregateColumns.value)
		const columnsData: any = {}

		tableLocalData.value = sortBy(data, 'displayOrder').map(row => {
			if (!row.rowState)
				row.rowState = RowStates.Unchanged

			let nextIndex = lastRowIndex + 1

			if (row.displayOrder && row.displayOrder === nextIndex)
				lastRowIndex = row.displayOrder
			else {
				row.displayOrder = nextIndex
				lastRowIndex = nextIndex
			}

			if (typeof row[tableFields.isValid] !== 'boolean')
				row[tableFields.isValid] = true

			if (!row[tableFields.id])
				row[tableFields.id] = `grid__${generateUUID()}`

			row[tableFields.invalidCells] = []
			row[tableFields.isLocked] = false
			row[tableFields.rowType] = 'row'

			if (isAggregationEnabled.value) {
				aggColNames.forEach(item => {
					if (columnsData[item])
						columnsData[item].push((row as any)[item])

					else
						columnsData[item] = [(row as any)[item]]
				})
			}

			return row
		})


		if (!data.length && gridOptions.value.canAddRow && gridOptions.value.isEditable && gridOptions.value.addRowOnMount) {
			if (addRowOnLoadTimeout.value)
				clearTimeout(addRowOnLoadTimeout.value)

			addRowOnLoadTimeout.value = setTimeout(() => {
				if (!data.length && gridOptions.value.canAddRow && gridOptions.value.isEditable) {
					addNewRow('end', false)
				}
				addRowOnLoadTimeout.value = null
			}, 500)

		}

		if (isAggregationEnabled.value) {
			const aggregateRow: any = {}
			aggColNames.forEach(item => {
				aggregateRow[item] = aggregateColumns.value[item](columnsData[item])
			})

			aggregateRow[tableFields.id] = `grid__${generateUUID()}_aggregate`
			aggregateRow[tableFields.rowType] = 'aggregate'

			pinnedBottomRow.value = [aggregateRow]
		}
	}

	function prepareColumns(cols: IExtendedColDef[], manageVisiblity: boolean = true) {
		if (manageVisiblity)
			gridIsVisible.value = false

		const validators: [string, IValidator[]][] = []
		const columns: IExtendedColDef[] = []

		for (let i = 0; i < cols.length; i++) {
			const column = cols[i]

			if (!column)
				continue

			column.columnId = `col__${generateUUID()}`

			if (column.validators)
				validators.push([`${column.field || ''}$${column.headerName || ''}`, column.validators])

			columns.push(column)
		}

		tableValidatorsMap = new Map(validators)
		colDefs.value = columns

		if (isAggregationEnabled.value) {
			prepareAggregationColumns(columns)
		}

		tryGetGridState()
	}

	function tryAddAggregationFunc(column: IExtendedColDef) {
		if (column.aggregateFunction && column.field) {
			let func

			if (typeof column.aggregateFunction === 'string')
				switch (column.aggregateFunction) {
					case 'avg':
						func = avg
						break
					case 'count':
						func = count
						break
					case 'max':
						func = max
						break
					case 'min':
						func = min
						break
					case 'sum':
						func = sum
						break
				}

			else
				func = column.aggregateFunction

			aggregateColumns.value[column.field] = func
		}
	}

	function prepareAndCalculateAggregation() {
		prepareAggregationColumns()
		calculateAllAggregations()
	}

	function prepareAggregationColumns(colDefs?: IExtendedColDef[]) {
		if (!gridApi.value && !colDefs) return

		const columns = colDefs ? colDefs : gridApi.value?.getColumnDefs() || []

		for (let i = 0; i < columns.length; i++) {
			tryAddAggregationFunc(columns[i])
		}
	}

	function calculateAllAggregations() {
		const columnKeys = Object.keys(aggregateColumns.value) as (keyof T)[]
		const columnsNodesData: TObjectWithFieldOf<T> = {}

		gridApi.value?.forEachNodeAfterFilter(node => {

			if (node.data?.rowState !== RowStates.Deleted) {

				for (let i = 0; i < columnKeys.length; i++) {

					const cellValue = gridApi.value?.getCellValue({ rowNode: node, colKey: columnKeys[i] as string })

					if (columnsNodesData[columnKeys[i]])
						columnsNodesData[columnKeys[i]].push(cellValue)
					else
						columnsNodesData[columnKeys[i]] = [cellValue]

				}
			}
		})

		if (isAggregationEnabled.value === true) {
			const aggregateRow: TObjectWithFieldOf<T> = {}
			columnKeys.forEach(item => {
				if (columnsNodesData[item])
					aggregateRow[item] = aggregateColumns.value[item](columnsNodesData[item])
			})

			aggregateRow[tableFields.id] = `grid__${generateUUID()}_aggregate`
			aggregateRow[tableFields.rowType] = 'aggregate'

			pinnedBottomRow.value = [aggregateRow]
		}
	}

	function getTableData(keepFocus: boolean = false, excludeLastRowIfEmpty: boolean = false) {
		if (!gridApi.value) return { data: [] }

		const editingCell = gridApi.value.getEditingCells()?.[0]

		gridApi.value.stopEditing()

		const allRows: T[] = []
		gridApi.value.forEachNode((node) => allRows.push(node.data as T))

		if (keepFocus && editingCell)
			gridApi.value.startEditingCell({
				rowIndex: editingCell.rowIndex,
				colKey: editingCell.column.getId()
			})

		if (excludeLastRowIfEmpty && allRows.length) {
			const lastRow = allRows.at(-1) as T

			const isEmpty = isEqual(removeTableFieldsFromData(lastRow), removeTableFieldsFromData(getNewRowData()))

			if (isEmpty)
				allRows.pop()
		}


		return { data: CloneDeep(allRows) }
	}

	function commitChanges() {
		return new Promise((resolve, reject) => {

			let shouldDeleteLastEmpties = true
			while (shouldDeleteLastEmpties) {
				shouldDeleteLastEmpties = tryDeleteLastEmptyRow()
			}

			if (!validateAllRows()) {
				reject()
				return
			}

			resolve(getTableData(true)?.data)
		})

	}

	function prepareOptions(options: IExtendedGridOptions) {
		gridOptions.value.initialState = getActiveState.value
		gridOptions.value = Merge(gridOptions.value, options)

		if (gridOptions.value.enableAggregation === false) {
			isAggregationEnabled.value = false
		}

		computeButtons(props.buttons)
	}

	function getContextMenuItems(): (string | MenuItemDef)[] {
		const customActions: MenuItemDef[] | undefined = props.actions?.map((act) => {
			return {
				name: act.name,
				shortcut: shortcutDisplay(act.shortcut),
				action: (params) => {
					act.handler(params.node?.data, params.node?.rowIndex, tableLocalData.value, params.column, params.api)
				}
			}
		})

		return [
			'cut',
			'copy',
			'copyWithHeaders',
			{
				name: 'چسباندن',
				shortcut: 'Ctrl + V',
				icon: '<span class="ag-icon ag-icon-paste"></span>',
				action: async (params) => {
					try {
						const text = await navigator.clipboard.readText()
						if (text && activeElementForPaste.value) {
							activeElementForPaste.value.value = text
							activeElementForPaste.value.dispatchEvent(new Event('input', { bubbles: true }))
						}
					}
					catch {

					}
				}

			},
			'separator',
			'export',
			'separator',
			...(customActions || [])
		] as (string | MenuItemDef)[]
	}

	function tryGetGridState() {
		if (getActiveState.value) {
			gridOptions.value.initialState = getActiveState.value.value
		}

		setTimeout(() => {
			gridIsVisible.value = true
		}, 10)
	}

	function resetGridState() {
		let currentState = getActiveState.value

		if (!currentState) return

		currentState.rowState = 'deleted'

		gridOptions.value.initialState = undefined

		store.dispatch('appStates/modify', [currentState])
		store.dispatch('appStates/sync').then(() => {
			prepareColumns(props.columns)
		})
	}

	function saveGridState(immediate: boolean = false) {
		if (gridSaveStateTimeout)
			clearTimeout(gridSaveStateTimeout)

		gridSaveStateTimeout = setTimeout(() => {
			let currentState = getActiveState.value
			if (!currentState) {
				currentState = {
					id: emptyId,
					key: `grid__${props.options.gridId}`,
					name: `grid__${props.options.gridId}`,
					isActive: true,
					rowState: 'new',
				}
			} else {
				currentState.rowState = 'changed'
			}

			const state = gridApi.value?.getState()

			if (state) {
				state.filter = undefined
				state.sort = undefined
			}

			currentState.value = state

			store.dispatch('appStates/modify', [currentState])
			store.dispatch('appStates/sync')
		}, immediate ? 0 : 5000)

	}

	function getMainMenuItems(): (MenuItemDef | string)[] {
		return [
			'columnChooser',
			'separator',
			'pinSubMenu',
			'separator',
			'autoSizeThis',
			'autoSizeAll',
			'separator',
			{
				name: t('ag-grid-local.saveColumnsState'),
				action: () => saveGridState(true)
			},
			{
				name: t('ag-grid-local.resetColumnsState'),
				action: resetGridState
			}
		]
	}

	function toggleColumnsVisibility(columnKeys: string[], isVisible: boolean) {
		if (!gridApi.value) return

		gridApi.value.setColumnsVisible(columnKeys, isVisible)

		columnKeys.forEach(columnField => {
			const column = gridApi.value?.getColumnDef(columnField)
			if (column) {
				column.suppressColumnsToolPanel = !isVisible
			}
		})

		gridApi.value.refreshHeader()
	}
	//#endregion Table Methods

	//#region Expose
	defineExpose({
		gridApi,
		getTableData,
		prepareData,
		commitChanges,
		prepareColumns,
		prepareOptions,
		computeButtons,
		addNewRow,
		addMultipleNewRows,
		updateRowData,
		deleteRow,
		deleteTargetRow,
		getActiveRowIndex,
		toggleColumnsVisibility,
		tryDeleteLastEmptyRow,
		colDefs
	})
	//#endregion Expose
</script>
